import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { useRole } from "@/contexts/RoleContext";
import { useMetiers, useCreateMetier, useDeleteMetier, metiersKeys } from "@/hooks/useMetiers";
import { metiersRepository } from "@/repositories/metiersRepository";
import type { MetierRow } from "@/schemas/metier";

export function MetiersPage() {
  const { t } = useTranslation();
  const { role } = useRole();
  const qc = useQueryClient();

  const { data: metiers, isLoading, isError } = useMetiers();
  const createMut = useCreateMetier();
  const deleteMut = useDeleteMetier();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingMetier, setEditingMetier] = useState<MetierRow | null>(null);
  const [formData, setFormData] = useState({ nom: "", couleur: "#6BB5A0" });
  const [isUpdating, setIsUpdating] = useState(false);

  // Toggle actif — mutation inline car l'id varie par ligne.
  const toggleActif = async (metier: MetierRow) => {
    try {
      await metiersRepository.update(role, metier.id, { actif: !metier.actif });
      qc.invalidateQueries({ queryKey: metiersKeys.all });
    } catch (err) {
      console.error(err);
    }
  };

  const removeMetier = (metier: MetierRow) => {
    if (!metier.id) return;
    deleteMut.mutate(metier.id);
  };

  const openEditModal = (metier: MetierRow) => {
    setEditingMetier(metier);
    setFormData({ nom: metier.nom ?? "", couleur: metier.couleur ?? "#6BB5A0" });
    setModalOpen(true);
  };

  const openAddModal = () => {
    setEditingMetier(null);
    setFormData({ nom: "", couleur: "#6BB5A0" });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingMetier(null);
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) return;
    if (editingMetier) {
      setIsUpdating(true);
      try {
        await metiersRepository.update(role, editingMetier.id, {
          nom: formData.nom,
          couleur: formData.couleur,
        });
        qc.invalidateQueries({ queryKey: metiersKeys.all });
        closeModal();
      } catch (err) {
        console.error(err);
      } finally {
        setIsUpdating(false);
      }
    } else {
      const id = formData.nom
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      createMut.mutate(
        { id, nom: formData.nom, couleur: formData.couleur, actif: true },
        { onSuccess: closeModal },
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-accent" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="bg-danger-light border border-danger rounded-md p-4">
        <p className="text-sm text-danger font-medium">{t("parametres.metiers.loadError")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <Button variant="primary" onClick={openAddModal}>
          <Plus size={16} />
          {t("parametres.metiers.add")}
        </Button>
      </div>

      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.metiers.color")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.name")}</th>
              <th className="text-left px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("parametres.metiers.active")}</th>
              <th className="text-right px-4 py-3 text-xs font-medium tracking-wider uppercase text-text-muted">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {(metiers ?? []).map((metier) => (
              <tr key={metier.id} className="border-b border-border last:border-b-0 hover:bg-gray-100 transition-colors">
                <td className="px-4 py-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: metier.couleur ?? "#ccc" }}
                  />
                </td>
                <td className="px-4 py-3 text-xs font-medium text-text-primary">{metier.nom}</td>
                <td className="px-4 py-3">
                  <Checkbox checked={!!metier.actif} onChange={() => toggleActif(metier)} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      className="p-1.5 rounded hover:bg-gray-200 cursor-pointer outline-none shadow-none"
                      onClick={() => openEditModal(metier)}
                    >
                      <Pencil size={14} className="text-text-muted" />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 rounded hover:bg-gray-200 cursor-pointer outline-none shadow-none"
                      onClick={() => removeMetier(metier)}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 size={14} className="text-text-muted hover:text-danger" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingMetier ? t("parametres.metiers.editTitle") : t("parametres.metiers.addTitle")}
      >
        <div className="flex flex-col gap-4">
          <FormField
            label={t("common.name")}
            value={formData.nom}
            onChange={(v) => setFormData((prev) => ({ ...prev, nom: v }))}
          />

          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium tracking-wider uppercase text-text-muted">
              {t("parametres.metiers.color")}
            </span>
            <div className="flex items-center gap-3 px-3 py-2 bg-surface border border-border rounded-md">
              <div
                className="w-6 h-6 rounded-full border border-border shrink-0"
                style={{ backgroundColor: formData.couleur }}
              />
              <input
                type="color"
                value={formData.couleur}
                onChange={(e) => setFormData((prev) => ({ ...prev, couleur: e.target.value }))}
                className="w-full h-7 cursor-pointer bg-transparent border-0 outline-none shadow-none p-0"
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 pt-3">
            <Button variant="cancel" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={createMut.isPending || isUpdating}
            >
              {createMut.isPending || isUpdating ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
